/* Onglet Communications : e-mails groupés, factures et rapports PDF, modèles d'e-mail.
   Repris de l'ancien onglet « Envoi » du CRM, sans la partie questionnaire (gérée par le pipeline). */
import React, { useState, useEffect, useRef } from 'react';
import { X, Search, ChevronDown, Send, CheckSquare, Square, Eye, Download, FileText, BarChart3, FileEdit as Edit3, Building2, Save, Trash2, BookOpen, PenLine, Receipt, TrendingUp, ChevronLeft, ChevronRight, Calendar, Settings2, Mail, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Participant {
  id: string;
  name: string;
  email: string | null;
  send_email: string | null;
  type: string;
  account_status: string | null;
  ean_code: string | null;
  groupe: string | null;
  sharing_operation_id: string;
  address?: string | null;
  company_number?: string | null;
  entry_date?: string | null;
  annual_consumption?: number | null;
}

interface SharingOperation {
  id: string;
  name: string;
}


interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  message: string;
  created_at: string;
  updated_at: string;
}

type FilterType = 'all' | 'consumer' | 'producer';
type SendType = 'invoice' | 'report' | 'simulation';
type WizardStep = 'type' | 'compose' | 'confirm';

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const PARTICIPANT_VARIABLES: { key: string; label: string; resolve: (p: Participant) => string }[] = [
  { key: '{{nom}}', label: 'Nom', resolve: p => p.name },
  { key: '{{email}}', label: 'Email', resolve: p => p.email || '' },
  { key: '{{type}}', label: 'Type', resolve: p => p.type === 'producer' ? 'Producteur' : 'Consommateur' },
  { key: '{{ean}}', label: 'Code EAN', resolve: p => p.ean_code || '' },
  { key: '{{adresse}}', label: 'Adresse', resolve: p => p.address || '' },
  { key: '{{n_entreprise}}', label: 'N° entreprise', resolve: p => p.company_number || '' },
  { key: '{{date_entree}}', label: 'Date d\'entrée', resolve: p => p.entry_date ? new Date(p.entry_date).toLocaleDateString('fr-BE') : '' },
  { key: '{{mois}}', label: 'Mois / période de facturation', resolve: () => MONTH_NAMES[new Date().getMonth()] },
  { key: '{{annee}}', label: 'Année courante', resolve: () => new Date().getFullYear().toString() },
];

const SEND_TYPES: { key: SendType; label: string; description: string; icon: React.ElementType; accent: string; badge: string }[] = [
  {
    key: 'invoice',
    label: 'Facture',
    description: 'Envoyez une facture PDF générée automatiquement pour une période donnée',
    icon: Receipt,
    accent: 'border-[#F8BD3D] bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
  },
  {
    key: 'report',
    label: 'Rapport',
    description: 'Envoyez un rapport de consommation annuel en pièce jointe',
    icon: BarChart3,
    accent: 'border-blue-300 bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
  },
  {
    key: 'simulation',
    label: 'Simulation',
    description: 'Envoyez un email de simulation ou d\'offre personnalisé',
    icon: TrendingUp,
    accent: 'border-emerald-300 bg-emerald-50',
    badge: 'bg-emerald-100 text-emerald-700',
  },
];

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function CommunicationsTab({ operationId }: { operationId?: string | null }) {
  /* ── Data ── */
  const [allParticipants, setAllParticipants] = useState<Participant[]>([]);
  const [sharingOperations, setSharingOperations] = useState<SharingOperation[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  /* ── Wizard state ── */
  const [wizardStep, setWizardStep] = useState<WizardStep>('type');
  const [sendType, setSendType] = useState<SendType | null>(null);

  /* ── Member selection ── */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState<FilterType>('all');
  const [memberOpFilter, setMemberOpFilter] = useState<string>('all');

  /* ── Email composer ── */
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [showVariables, setShowVariables] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  /* ── Templates ── */
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  /* ── Invoice / Report params ── */
  const [invoicePeriod, setInvoicePeriod] = useState({ startMonth: '', endMonth: '' });
  const [isAnnualRegularization, setIsAnnualRegularization] = useState(false);
  const [reportYear, setReportYear] = useState(new Date().getFullYear().toString());

  /* ── Sending ── */
  const [sending, setSending] = useState(false);

  /* ── Download panel ── */
  const [showDownloadPanel, setShowDownloadPanel] = useState<'invoice' | 'report' | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkReportDownloading, setBulkReportDownloading] = useState(false);
  const [bulkPeriod, setBulkPeriod] = useState({ startMonth: '', endMonth: '' });
  const [bulkIncludeMembershipFee, setBulkIncludeMembershipFee] = useState(false);
  const [bulkIsAnnualRegularization, setBulkIsAnnualRegularization] = useState(false);
  const [bulkReportYear, setBulkReportYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    loadData();
    loadTemplates();
  }, [operationId]);

  const loadData = async () => {
    setLoadingParticipants(true);
    try {
      const [{ data: pData }, { data: opData }] = await Promise.all([
        supabase.from('participants').select('id,name,email,send_email,type,account_status,ean_code,groupe,sharing_operation_id,address,company_number,entry_date,annual_consumption').order('name'),
        supabase.from('sharing_operations').select('id,name').is('deleted_at', null).order('name'),
      ]);
      setAllParticipants(pData || []);
      setSharingOperations(opData || []);
    } catch { toast.error('Erreur lors du chargement des membres'); }
    finally { setLoadingParticipants(false); }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await supabase.from('email_templates').select('*').order('name');
      setEmailTemplates(data || []);
    } catch { } finally { setLoadingTemplates(false); }
  };

  const getEffectiveSendEmail = (p: Participant) => p.send_email || p.email;

  const getPeriodLabel = (start: string, end: string): string => {
    if (!start || !end) return MONTH_NAMES[new Date().getMonth()];
    const [startYear, startMonthIdx] = start.split('-').map(Number);
    const [endYear, endMonthIdx] = end.split('-').map(Number);
    if (startYear === endYear && startMonthIdx === endMonthIdx) {
      return `${MONTH_NAMES[startMonthIdx - 1]} ${startYear}`;
    }
    return `${MONTH_NAMES[startMonthIdx - 1]} ${startYear} – ${MONTH_NAMES[endMonthIdx - 1]} ${endYear}`;
  };

  const resolveText = (template: string, p: Participant, overrides?: Record<string, string>) => {
    let result = template;
    PARTICIPANT_VARIABLES.forEach(v => {
      const value = overrides && v.key in overrides ? overrides[v.key] : v.resolve(p);
      result = result.replace(new RegExp(v.key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return result;
  };

  /* ── Filtered participants for wizard ── */
  const wizardParticipants = allParticipants.filter(p => {
    if (operationId) return p.sharing_operation_id === operationId;
    if (memberOpFilter !== 'all') return p.sharing_operation_id === memberOpFilter;
    return true;
  }).filter(p => {
    const q = memberSearch.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q) || (p.ean_code || '').toLowerCase().includes(q);
    const matchType = memberTypeFilter === 'all' || p.type === memberTypeFilter;
    return matchSearch && matchType;
  });

  const selectedParticipants = allParticipants.filter(p => selectedIds.has(p.id));
  const selectedWithEmail = selectedParticipants.filter(p => getEffectiveSendEmail(p));
  const previewParticipant = selectedWithEmail[previewIdx] || selectedWithEmail[0] || null;

  const toggleSelect = (id: string) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    if (selectedIds.size === wizardParticipants.length && wizardParticipants.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(wizardParticipants.map(p => p.id)));
    }
  };

  const insertVariable = (variable: string, target: 'message' | 'subject') => {
    if (target === 'message' && messageRef.current) {
      const el = messageRef.current;
      const start = el.selectionStart ?? message.length;
      const end = el.selectionEnd ?? message.length;
      setMessage(message.slice(0, start) + variable + message.slice(end));
      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + variable.length; el.focus(); });
    } else if (target === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      setSubject(subject.slice(0, start) + variable + subject.slice(end));
      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + variable.length; el.focus(); });
    }
  };

  /* ── Select type ── */
  const handleSelectType = (type: SendType) => {
    setSendType(type);
    setSelectedIds(new Set());
    setMemberSearch('');
    setMemberTypeFilter('all');
    if (type === 'invoice') {
      setSubject('Facture {{mois}} - Sun Is Up');
      setMessage('Bonjour {{nom}},\n\nVeuillez trouver ci-joint votre facture pour la période sélectionnée.\n\nCordialement,\nL\'équipe Sun Is Up');
    } else if (type === 'report') {
      setSubject('Rapport annuel {{annee}} - Sun Is Up');
      setMessage('Bonjour {{nom}},\n\nVeuillez trouver ci-joint votre rapport de consommation annuel.\n\nCordialement,\nL\'équipe Sun Is Up');
    } else {
      setSubject('Simulation - Sun Is Up');
      setMessage('Bonjour {{nom}},\n\nVeuillez trouver ci-joint votre simulation.\n\nCordialement,\nL\'équipe Sun Is Up');
    }
    setActiveTemplateId(null);
    setWizardStep('compose');
  };

  const canProceedToConfirm = () => {
    if (selectedIds.size === 0) return false;
    if (!subject.trim() || !message.trim()) return false;
    return true;
  };

  const canSend = () => {
    if (!canProceedToConfirm()) return false;
    if (sendType === 'invoice') return !!invoicePeriod.startMonth && !!invoicePeriod.endMonth;
    if (sendType === 'report') return !!reportYear;
    return true;
  };

  /* ── Templates ── */
  const handleSaveTemplate = async (overwriteId?: string) => {
    if (!newTemplateName.trim() && !overwriteId) { toast.error('Donnez un nom au template'); return; }
    setSavingTemplate(true);
    try {
      if (overwriteId) {
        const { error } = await supabase.from('email_templates').update({ subject, message, updated_at: new Date().toISOString() }).eq('id', overwriteId);
        if (error) throw error;
        toast.success('Template mis à jour');
      } else {
        const { data, error } = await supabase.from('email_templates').insert({ name: newTemplateName.trim(), subject, message }).select('id').maybeSingle();
        if (error) throw error;
        if (data) setActiveTemplateId(data.id);
        toast.success('Template enregistré');
      }
      setNewTemplateName(''); setShowSaveDialog(false);
      await loadTemplates();
    } catch { toast.error('Erreur lors de l\'enregistrement'); } finally { setSavingTemplate(false); }
  };

  const handleLoadTemplate = (tpl: EmailTemplate) => {
    setSubject(tpl.subject); setMessage(tpl.message);
    setActiveTemplateId(tpl.id); setShowTemplatePanel(false);
    toast.success(`Template "${tpl.name}" chargé`);
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    const { error } = await supabase.from('email_templates').delete().eq('id', id);
    if (error) { toast.error('Erreur lors de la suppression'); return; }
    if (activeTemplateId === id) setActiveTemplateId(null);
    toast.success(`Template "${name}" supprimé`);
    await loadTemplates();
  };

  /* ── Send ── */
  const handleSend = async () => {
    if (!canSend()) return;
    setSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const attachmentsMap: Record<string, string> = {};

      if (sendType === 'invoice') {
        const toastId = toast.loading('Génération des factures PDF...');
        const { generateInvoicePDF } = await import('../InvoiceTemplate');
        const { data: fullP } = await supabase.from('participants').select('*').in('id', Array.from(selectedIds));
        if (fullP) {
          for (const fp of fullP) {
            try {
              const blob = await generateInvoicePDF(fp, invoicePeriod, undefined, isAnnualRegularization);
              if (blob) attachmentsMap[fp.id] = await blobToBase64(blob);
            } catch (e) { console.error('PDF error', fp.name, e); }
          }
        }
        toast.dismiss(toastId);
      } else if (sendType === 'report') {
        const toastId = toast.loading('Génération des rapports PDF...');
        const { generateReportPDF } = await import('../ParticipantReportModal');
        const { data: fullP } = await supabase.from('participants').select('*').in('id', Array.from(selectedIds));
        if (fullP) {
          for (const fp of fullP) {
            try {
              const blob = await generateReportPDF(fp, reportYear);
              if (blob) attachmentsMap[fp.id] = await blobToBase64(blob);
            } catch (e) { console.error('Report error', fp.name, e); }
          }
        }
        toast.dismiss(toastId);
      }

      const periodOverrides: Record<string, string> | undefined = sendType === 'invoice' && invoicePeriod.startMonth && invoicePeriod.endMonth
        ? {
            '{{mois}}': getPeriodLabel(invoicePeriod.startMonth, invoicePeriod.endMonth),
            '{{annee}}': invoicePeriod.startMonth.split('-')[0],
          }
        : undefined;

      const recipients = selectedWithEmail.map(p => ({
        name: p.name,
        email: getEffectiveSendEmail(p)!,
        subject: resolveText(subject, p, periodOverrides),
        message: resolveText(message, p, periodOverrides),
        attachmentBase64: attachmentsMap[p.id],
        attachmentFilename: sendType === 'invoice'
          ? `Facture_${p.name.replace(/\s+/g, '_')}_${invoicePeriod.startMonth}_${invoicePeriod.endMonth}.pdf`
          : sendType === 'report'
          ? `Rapport_${p.name.replace(/\s+/g, '_')}_${reportYear}.pdf`
          : undefined,
      }));

      const edgeUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-member-email`;
      const edgeRes = await fetch(edgeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ recipients }),
      });
      const edgeResult = await edgeRes.json().catch(() => ({}));
      if (!edgeRes.ok) throw new Error(edgeResult.error || `Erreur ${edgeRes.status} lors de l'envoi`);
      const successCount = edgeResult.successCount ?? selectedWithEmail.length;

      const pjLabel = (sendType === 'invoice' || sendType === 'report') ? ' avec pièce jointe PDF' : '';
      toast.success(`${successCount} email(s) envoyé(s)${pjLabel}`);
      setWizardStep('type');
      setSendType(null);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setSending(false); }
  };

  /* ── Bulk downloads ── */
  const handleBulkDownloadInvoices = async () => {
    if (!bulkPeriod.startMonth || !bulkPeriod.endMonth) { toast.error('Sélectionnez une période'); return; }
    if (selectedIds.size === 0) { toast.error('Sélectionnez au moins un membre'); return; }
    setBulkDownloading(true);
    setShowDownloadPanel(null);
    try {
      const JSZip = (await import('jszip')).default;
      const { generateInvoicePDF, removeGroupePrefix, normalizeFileName } = await import('../InvoiceTemplate');
      const { data: fullP } = await supabase.from('participants').select('*').in('id', Array.from(selectedIds));
      if (!fullP?.length) { toast.error('Impossible de charger les données'); return; }
      const zip = new JSZip();
      const gMap = new Map<string, typeof fullP[0][]>();
      const singles: typeof fullP[0][] = [];
      fullP.forEach(p => { if (p.groupe) { if (!gMap.has(p.groupe)) gMap.set(p.groupe, []); gMap.get(p.groupe)!.push(p); } else { singles.push(p); } });
      const toProcess = [...Array.from(gMap.values()).map(g => g[0]), ...singles];
      const tid = toast.loading(`Génération de ${toProcess.length} factures...`);
      let cnt = 0;
      for (let i = 0; i < toProcess.length; i++) {
        const p = toProcess[i];
        toast.loading(`${i + 1}/${toProcess.length}: ${p.groupe || p.name}...`, { id: tid });
        try {
          const blob = await generateInvoicePDF(p, bulkPeriod, p.groupe ? bulkIncludeMembershipFee : undefined, p.type !== 'producer' ? bulkIsAnnualRegularization : undefined);
          if (blob) { zip.file(`Facture_${normalizeFileName(p.groupe ? removeGroupePrefix(p.groupe) : p.name)}_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.pdf`, blob); cnt++; }
        } catch (e) { console.error(e); }
        await new Promise(r => setTimeout(r, 100));
      }
      toast.dismiss(tid);
      if (!cnt) { toast.error('Aucune facture générée'); return; }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a'); a.href = url; a.download = `Factures_${bulkPeriod.startMonth}-${bulkPeriod.endMonth}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${cnt} factures téléchargées`);
    } catch (e) { console.error(e); toast.error('Erreur'); } finally { setBulkDownloading(false); }
  };

  const handleBulkDownloadReports = async () => {
    if (selectedIds.size === 0) { toast.error('Sélectionnez au moins un membre'); return; }
    setBulkReportDownloading(true);
    setShowDownloadPanel(null);
    try {
      const JSZip = (await import('jszip')).default;
      const { generateReportPDF } = await import('../ParticipantReportModal');
      const { normalizeFileName } = await import('../InvoiceTemplate');
      const { data: fullP } = await supabase.from('participants').select('*').in('id', Array.from(selectedIds));
      if (!fullP?.length) { toast.error('Impossible de charger les données'); return; }
      const zip = new JSZip();
      const tid = toast.loading(`Génération de ${fullP.length} rapports...`);
      let cnt = 0;
      for (let i = 0; i < fullP.length; i++) {
        const p = fullP[i];
        toast.loading(`${i + 1}/${fullP.length}: ${p.name}...`, { id: tid });
        try { const blob = await generateReportPDF(p, bulkReportYear); if (blob) { zip.file(`Rapport_${normalizeFileName(p.name)}_${bulkReportYear}.pdf`, blob); cnt++; } } catch (e) { console.error(e); }
        await new Promise(r => setTimeout(r, 500));
      }
      toast.dismiss(tid);
      if (!cnt) { toast.error('Aucun rapport généré'); return; }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a'); a.href = url; a.download = `Rapports_${bulkReportYear}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${cnt} rapports téléchargés`);
    } catch (e) { console.error(e); toast.error('Erreur'); } finally { setBulkReportDownloading(false); }
  };


  const sendTypeInfo = sendType ? SEND_TYPES.find(t => t.key === sendType) : null;
  const needsAttachment = sendType === 'invoice' || sendType === 'report';

  return (
      <div className="flex-1 overflow-hidden flex flex-col">

        {/* ── STEP 1: Choose type ── */}
        {wizardStep === 'type' && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto">
              <div className="mb-8 text-center">
                <h3 className="text-xl font-semibold text-neutral-900 mb-1">Que souhaitez-vous envoyer ?</h3>
                <p className="text-sm text-neutral-500">Choisissez le type d'envoi pour continuer</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {SEND_TYPES.map(({ key, label, description, icon: Icon, accent, badge }) => (
                  <button
                    key={key}
                    onClick={() => handleSelectType(key)}
                    className={`group flex flex-col items-start gap-3 p-5 rounded-xl border-2 transition-all hover:shadow-md text-left ${
                      sendType === key ? accent : 'border-neutral-100 bg-white hover:border-neutral-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${badge}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900 mb-0.5">{label}</p>
                      <p className="text-xs text-neutral-500 leading-relaxed">{description}</p>
                    </div>
                    <div className="mt-auto flex items-center gap-1 text-xs font-medium text-neutral-400 group-hover:text-neutral-600 transition-colors">
                      Sélectionner <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </button>
                ))}
              </div>

              {/* Download tools */}
              <div className="mt-8 border border-neutral-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100">
                  <div className="flex items-center gap-2">
                    <Download className="w-4 h-4 text-neutral-500" />
                    <span className="text-sm font-semibold text-neutral-700">Téléchargements en masse</span>
                  </div>
                  <span className="text-[11px] text-neutral-400">ZIP de PDFs</span>
                </div>
                <div className="p-4 space-y-3">
                  {/* Invoice download */}
                  <div>
                    <button
                      onClick={() => setShowDownloadPanel(showDownloadPanel === 'invoice' ? null : 'invoice')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#F8BD3D]" />
                        <span className="text-sm font-medium text-neutral-700">Factures PDF</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${showDownloadPanel === 'invoice' ? 'rotate-180' : ''}`} />
                    </button>
                    {showDownloadPanel === 'invoice' && (
                      <div className="mt-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-neutral-600 block mb-1">Membres (cochez dans liste à gauche)</label>
                            <p className="text-[11px] text-neutral-500">{selectedIds.size} sélectionné(s)</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-neutral-600 block mb-1">Mois début</label>
                            <input type="month" value={bulkPeriod.startMonth} onChange={e => setBulkPeriod(p => ({ ...p, startMonth: e.target.value }))} className="w-full px-2 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-neutral-600 block mb-1">Mois fin</label>
                            <input type="month" value={bulkPeriod.endMonth} onChange={e => setBulkPeriod(p => ({ ...p, endMonth: e.target.value }))} className="w-full px-2 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50" />
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={bulkIncludeMembershipFee} onChange={e => setBulkIncludeMembershipFee(e.target.checked)} className="rounded" /><span className="text-[10px] text-neutral-600">Cotisation (60.50€ TTC)</span></label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={bulkIsAnnualRegularization} onChange={e => setBulkIsAnnualRegularization(e.target.checked)} className="rounded" /><span className="text-[10px] text-neutral-600">Régularisation annuelle</span></label>
                        <button onClick={handleBulkDownloadInvoices} disabled={bulkDownloading || selectedIds.size === 0 || !bulkPeriod.startMonth || !bulkPeriod.endMonth} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
                          <Download className="w-3.5 h-3.5" />{bulkDownloading ? 'Génération...' : `Télécharger (${selectedIds.size})`}
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Report download */}
                  <div>
                    <button
                      onClick={() => setShowDownloadPanel(showDownloadPanel === 'report' ? null : 'report')}
                      className="w-full flex items-center justify-between px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-neutral-500" />
                        <span className="text-sm font-medium text-neutral-700">Rapports PDF</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${showDownloadPanel === 'report' ? 'rotate-180' : ''}`} />
                    </button>
                    {showDownloadPanel === 'report' && (
                      <div className="mt-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
                        <div>
                          <label className="text-[10px] font-medium text-neutral-600 block mb-1">Année</label>
                          <select value={bulkReportYear} onChange={e => setBulkReportYear(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50">
                            {['2023', '2024', '2025', '2026'].map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <button onClick={handleBulkDownloadReports} disabled={bulkReportDownloading || selectedIds.size === 0} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-neutral-800 hover:bg-neutral-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
                          <Download className="w-3.5 h-3.5" />{bulkReportDownloading ? 'Génération...' : `Télécharger (${selectedIds.size})`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ── STEP 2: Compose (template + member selection) ── */}
        {wizardStep === 'compose' && sendTypeInfo && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Step header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 bg-neutral-50 shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setWizardStep('type'); setSendType(null); }}
                  className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Retour
                </button>
                <div className="w-px h-4 bg-neutral-200" />
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sendTypeInfo.badge}`}>
                  {React.createElement(sendTypeInfo.icon, { className: 'w-3.5 h-3.5' })}
                  {sendTypeInfo.label}
                </div>
                <span className="text-xs text-neutral-500">Étape 1/2 — Composer et sélectionner les destinataires</span>
              </div>
                <button
                  onClick={() => setWizardStep('confirm')}
                  disabled={!canProceedToConfirm()}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-neutral-900 hover:bg-neutral-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Two-column layout: left=email composer, right=member list */}
            <div className="flex-1 overflow-hidden flex">

              {/* Left: Email composer */}
              <div className="flex-1 flex flex-col overflow-hidden border-r border-neutral-100">
                <div className="flex-1 overflow-y-auto p-4 space-y-4">

                  {/* Templates */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-neutral-700">Template email</span>
                      <div className="flex items-center gap-1.5">
                        {activeTemplateId && (
                          <button
                            onClick={() => handleSaveTemplate(activeTemplateId)}
                            disabled={savingTemplate}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 border border-neutral-200 rounded-lg text-neutral-500 hover:bg-neutral-50 transition-colors"
                            title="Mettre à jour ce template"
                          >
                            <Save className="w-3 h-3" /> Mettre à jour
                          </button>
                        )}
                        <button
                          onClick={() => { setShowSaveDialog(v => !v); setNewTemplateName(''); }}
                          className="flex items-center gap-1 text-[10px] px-2 py-1 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 font-medium rounded-lg transition-colors"
                        >
                          <PenLine className="w-3 h-3" /> Nouveau
                        </button>
                        <button
                          onClick={() => { setShowTemplatePanel(v => !v); }}
                          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors ${showTemplatePanel ? 'bg-neutral-200 border-neutral-300' : 'border-neutral-200 text-neutral-500 hover:bg-neutral-100'}`}
                        >
                          <BookOpen className="w-3 h-3" />
                          Templates {emailTemplates.length > 0 && <span className="text-[9px] bg-neutral-300 text-neutral-700 rounded-full px-1 ml-0.5">{emailTemplates.length}</span>}
                        </button>
                      </div>
                    </div>

                    {showSaveDialog && (
                      <div className="flex gap-2 items-center mb-2">
                        <input
                          type="text"
                          placeholder="Nom du template..."
                          value={newTemplateName}
                          onChange={e => setNewTemplateName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveTemplate(); }}
                          autoFocus
                          className="flex-1 px-2 py-1.5 text-xs border border-[#F8BD3D] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                        />
                        <button onClick={() => handleSaveTemplate()} disabled={savingTemplate || !newTemplateName.trim()} className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
                          <Save className="w-3 h-3" /> {savingTemplate ? '...' : 'Sauver'}
                        </button>
                        <button onClick={() => setShowSaveDialog(false)} className="p-1.5 text-neutral-400 hover:text-neutral-600"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    )}

                    {showTemplatePanel && (
                      <div className="mb-3 border border-neutral-200 rounded-xl overflow-hidden">
                        {loadingTemplates ? (
                          <p className="text-[10px] text-neutral-400 p-3">Chargement...</p>
                        ) : emailTemplates.length === 0 ? (
                          <p className="text-[10px] text-neutral-400 p-3 italic">Aucun template enregistré</p>
                        ) : (
                          <div className="max-h-40 overflow-y-auto divide-y divide-neutral-50">
                            {emailTemplates.map(tpl => (
                              <div key={tpl.id} className={`flex items-center gap-2 px-3 py-2 ${activeTemplateId === tpl.id ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}>
                                <button onClick={() => handleLoadTemplate(tpl)} className="flex-1 text-left min-w-0">
                                  <p className="text-xs font-medium text-neutral-800 truncate">{tpl.name}</p>
                                  <p className="text-[10px] text-neutral-400 truncate">{tpl.subject}</p>
                                </button>
                                <button onClick={() => handleDeleteTemplate(tpl.id, tpl.name)} className="p-1 text-neutral-300 hover:text-red-500 transition-colors shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeTemplateId && (
                      <div className="mb-2 text-[10px] text-[#C88F20] bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                        Template actif : {emailTemplates.find(t => t.id === activeTemplateId)?.name}
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="text-xs font-semibold text-neutral-700 block mb-1.5">Objet de l'email</label>
                    <input
                      ref={subjectRef}
                      type="text"
                      value={subject}
                      onChange={e => { setSubject(e.target.value); setActiveTemplateId(null); }}
                      className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                      placeholder="Objet..."
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-neutral-700">Message</label>
                      <button
                        onClick={() => setShowVariables(v => !v)}
                        className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors ${showVariables ? 'bg-[#F8BD3D] border-[#E5A935] text-neutral-900 font-medium' : 'border-neutral-200 text-neutral-500 hover:bg-neutral-100'}`}
                      >
                        <Edit3 className="w-3 h-3" /> Variables
                      </button>
                    </div>
                    <textarea
                      ref={messageRef}
                      value={message}
                      onChange={e => { setMessage(e.target.value); setActiveTemplateId(null); }}
                      rows={5}
                      className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50 resize-none"
                      placeholder="Rédigez votre message..."
                    />
                  </div>

                  {showVariables && (
                    <div className="border border-[#F8BD3D]/40 rounded-xl p-3 bg-amber-50/40">
                      <p className="text-[10px] font-semibold text-neutral-600 mb-2 uppercase tracking-wide">Variables personnalisées — cliquez pour insérer</p>
                      <p className="text-[10px] text-neutral-400 mb-2">Placez votre curseur dans le champ cible avant de cliquer.</p>
                      <div className="grid grid-cols-3 gap-1.5 mb-2">
                        {PARTICIPANT_VARIABLES.map(v => (
                          <button
                            key={v.key}
                            onClick={() => insertVariable(v.key, 'message')}
                            className="flex flex-col items-start px-2 py-1.5 bg-white border border-neutral-200 hover:border-[#F8BD3D] hover:bg-amber-50 rounded-lg transition-colors text-left group"
                          >
                            <span className="text-[10px] font-mono text-[#E5A935] font-semibold truncate w-full">{v.key}</span>
                            <span className="text-[9px] text-neutral-400">{v.label}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => insertVariable(PARTICIPANT_VARIABLES[0].key, 'subject')}
                        className="text-[10px] px-2 py-1 bg-white border border-neutral-200 hover:border-[#F8BD3D] rounded-md text-neutral-600 hover:text-neutral-800 transition-colors"
                      >
                        {'+ Insérer {{nom}} dans l\'objet'}
                      </button>
                    </div>
                  )}

                  {/* Preview */}
                  {selectedWithEmail.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <button
                          onClick={() => setShowPreview(v => !v)}
                          className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {showPreview ? 'Masquer aperçu' : 'Aperçu personnalisé'}
                        </button>
                        {showPreview && selectedWithEmail.length > 1 && (
                          <div className="flex items-center gap-1">
                            <button onClick={() => setPreviewIdx(i => Math.max(0, i - 1))} disabled={previewIdx === 0} className="p-0.5 disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
                            <span className="text-[10px] text-neutral-400">{previewIdx + 1}/{selectedWithEmail.length}</span>
                            <button onClick={() => setPreviewIdx(i => Math.min(selectedWithEmail.length - 1, i + 1))} disabled={previewIdx >= selectedWithEmail.length - 1} className="p-0.5 disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                      {showPreview && previewParticipant && (
                        <div className="border border-neutral-200 rounded-xl p-3 bg-neutral-50 text-xs">
                          <p className="font-semibold text-neutral-700 mb-1">Aperçu — {previewParticipant.name}</p>
                          <p className="text-neutral-500 mb-1"><span className="font-medium text-neutral-600">Objet :</span> {resolveText(subject, previewParticipant, sendType === 'invoice' && invoicePeriod.startMonth && invoicePeriod.endMonth ? { '{{mois}}': getPeriodLabel(invoicePeriod.startMonth, invoicePeriod.endMonth), '{{annee}}': invoicePeriod.startMonth.split('-')[0] } : undefined)}</p>
                          <p className="text-neutral-600 whitespace-pre-wrap border-t border-neutral-200 pt-2 mt-1">{resolveText(message, previewParticipant, sendType === 'invoice' && invoicePeriod.startMonth && invoicePeriod.endMonth ? { '{{mois}}': getPeriodLabel(invoicePeriod.startMonth, invoicePeriod.endMonth), '{{annee}}': invoicePeriod.startMonth.split('-')[0] } : undefined)}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {needsAttachment && (
                    <div className="flex items-center gap-2 text-[11px] text-neutral-500 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg">
                      <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      Le PDF sera généré automatiquement et joint à chaque email (paramètres à l'étape suivante)
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Member list */}
              <div className="w-64 flex flex-col overflow-hidden bg-white">

                <div className="p-3 border-b border-neutral-100 space-y-2 shrink-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-neutral-700">Destinataires</span>
                    <span className="text-[10px] text-neutral-400">{selectedIds.size} sél.</span>
                  </div>

                  {!operationId && sharingOperations.length > 0 && (
                    <div className="relative">
                      <Building2 className="w-3 h-3 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <select
                        value={memberOpFilter}
                        onChange={e => { setMemberOpFilter(e.target.value); }}
                        className="w-full pl-6 pr-2 py-1.5 text-[11px] border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50 bg-white appearance-none"
                      >
                        <option value="all">Toutes les opérations</option>
                        {sharingOperations.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="relative">
                    <Search className="w-3 h-3 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      className="w-full pl-6 pr-2 py-1.5 text-[11px] border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                    />
                  </div>

                  <div className="flex gap-1">
                    {(['all', 'consumer', 'producer'] as FilterType[]).map(f => (
                      <button key={f} onClick={() => setMemberTypeFilter(f)} className={`flex-1 text-[10px] py-1 rounded-md transition-colors ${memberTypeFilter === f ? 'bg-[#F8BD3D] text-neutral-900 font-semibold' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}>
                        {f === 'all' ? 'Tous' : f === 'consumer' ? 'Conso.' : 'Prod.'}
                      </button>
                    ))}
                  </div>

                  <button onClick={toggleSelectAll} className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-neutral-800 transition-colors">
                    {selectedIds.size === wizardParticipants.length && wizardParticipants.length > 0
                      ? <CheckSquare className="w-3.5 h-3.5 text-[#F8BD3D]" />
                      : <Square className="w-3.5 h-3.5" />}
                    Tout sélectionner
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loadingParticipants ? (
                    <div className="p-4 text-center text-xs text-neutral-400">Chargement...</div>
                  ) : wizardParticipants.length === 0 ? (
                    <div className="p-4 text-center text-xs text-neutral-400">Aucun membre</div>
                  ) : wizardParticipants.map(p => (
                    <div
                      key={p.id}
                      onClick={() => toggleSelect(p.id)}
                      className={`flex items-start gap-2 px-3 py-2 cursor-pointer border-b border-neutral-50 transition-colors ${selectedIds.has(p.id) ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}
                    >
                      <div className="mt-0.5 shrink-0">
                        {selectedIds.has(p.id) ? <CheckSquare className="w-3.5 h-3.5 text-[#F8BD3D]" /> : <Square className="w-3.5 h-3.5 text-neutral-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[11px] font-medium text-neutral-800 truncate">{p.name}</span>
                          <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full shrink-0 ${p.type === 'producer' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                            {p.type === 'producer' ? 'P' : 'C'}
                          </span>
                        </div>
                        <div className="text-[10px] text-neutral-400 truncate">
                          {getEffectiveSendEmail(p) || <span className="italic">Pas d'email</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer with count */}
                <div className="p-3 border-t border-neutral-100 shrink-0">
                  <div className="text-[10px] text-neutral-500 text-center">
                    {selectedIds.size === 0
                      ? 'Sélectionnez des destinataires'
                      : `${selectedWithEmail.length} avec email / ${selectedIds.size} sél.`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Confirm + file params ── */}
        {wizardStep === 'confirm' && sendTypeInfo && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Step header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 bg-neutral-50 shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setWizardStep('compose')}
                  className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Retour
                </button>
                <div className="w-px h-4 bg-neutral-200" />
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sendTypeInfo.badge}`}>
                  {React.createElement(sendTypeInfo.icon, { className: 'w-3.5 h-3.5' })}
                  {sendTypeInfo.label}
                </div>
                <span className="text-xs text-neutral-500">Étape 2/2 — {needsAttachment ? 'Paramètres du fichier et' : ''} Confirmation</span>
              </div>
              <button
                onClick={handleSend}
                disabled={sending || !canSend()}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? 'Envoi en cours...' : `Envoyer à ${selectedWithEmail.length} contact${selectedWithEmail.length > 1 ? 's' : ''}`}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="max-w-2xl mx-auto space-y-5">

                {/* Invoice params */}
                {sendType === 'invoice' && (
                  <div className="border border-amber-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-200">
                      <Calendar className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-900">Paramètres de la facture</span>
                      <span className="text-[10px] text-amber-600 ml-auto">Champs obligatoires</span>
                    </div>
                    <div className="p-4 space-y-3 bg-white">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-neutral-700 block mb-1.5">Mois de début <span className="text-red-500">*</span></label>
                          <input
                            type="month"
                            value={invoicePeriod.startMonth}
                            onChange={e => setInvoicePeriod(p => ({ ...p, startMonth: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-700 block mb-1.5">Mois de fin <span className="text-red-500">*</span></label>
                          <input
                            type="month"
                            value={invoicePeriod.endMonth}
                            onChange={e => setInvoicePeriod(p => ({ ...p, endMonth: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                          />
                        </div>
                      </div>
                      <div className="space-y-2 pt-1">
                        <p className="text-xs font-semibold text-neutral-700 mb-2">Options</p>
                        <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-100 bg-amber-50">
                          <div>
                            <p className="text-xs font-medium text-amber-800">Cotisation annuelle automatique</p>
                            <p className="text-[10px] text-amber-600">Incluse automatiquement au mois anniversaire d'entrée</p>
                          </div>
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer p-2.5 rounded-lg border border-neutral-100 hover:bg-neutral-50 transition-colors">
                          <input type="checkbox" checked={isAnnualRegularization} onChange={e => setIsAnnualRegularization(e.target.checked)} className="rounded w-3.5 h-3.5" />
                          <div>
                            <p className="text-xs font-medium text-neutral-700">Régularisation annuelle</p>
                            <p className="text-[10px] text-neutral-400">Régularisation annuelle des accises</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Report params */}
                {sendType === 'report' && (
                  <div className="border border-blue-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border-b border-blue-200">
                      <Settings2 className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-900">Paramètres du rapport</span>
                    </div>
                    <div className="p-4 bg-white">
                      <label className="text-xs font-medium text-neutral-700 block mb-1.5">Année <span className="text-red-500">*</span></label>
                      <select
                        value={reportYear}
                        onChange={e => setReportYear(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                      >
                        {['2023', '2024', '2025', '2026'].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Email summary */}
                <div className="border border-neutral-200 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                    <Mail className="w-4 h-4 text-neutral-500" />
                    <span className="text-sm font-semibold text-neutral-700">Récapitulatif de l'envoi</span>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Objet</p>
                        <p className="text-neutral-800 font-medium">{subject || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Type</p>
                        <p className="text-neutral-800 font-medium capitalize">{sendTypeInfo.label}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Message (aperçu)</p>
                      <p className="text-xs text-neutral-600 whitespace-pre-wrap line-clamp-3">{message || '—'}</p>
                    </div>
                    {needsAttachment && (
                      <div className="flex items-center gap-2 text-[11px] text-neutral-500 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg">
                        <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        PDF généré individuellement et joint pour chaque destinataire
                      </div>
                    )}
                  </div>
                </div>

                {/* Recipients summary */}
                <div className="border border-neutral-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-neutral-500" />
                      <span className="text-sm font-semibold text-neutral-700">Destinataires ({selectedWithEmail.length})</span>
                    </div>
                    {selectedParticipants.length > selectedWithEmail.length && (
                      <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        {selectedParticipants.length - selectedWithEmail.length} sans email (ignoré{selectedParticipants.length - selectedWithEmail.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                  <div className="p-3 max-h-44 overflow-y-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {selectedWithEmail.map(p => (
                        <div key={p.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-50 border border-neutral-100 rounded-lg">
                          <div className={`w-1.5 h-1.5 rounded-full ${p.type === 'producer' ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                          <span className="text-[11px] font-medium text-neutral-700">{p.name}</span>
                          <span className="text-[9px] text-neutral-400">{getEffectiveSendEmail(p)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
  );
}
